<template>
  <modal :open="internalOpen" :width="800">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.driver.details.sections.box.deleteBoxModal.title') }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <p class="pb-10">
        {{ $t('pages.driver.details.sections.box.deleteBoxModal.text') }}
      </p>
      <div class="flex flex-col gap-y-8 w-full justify-around mb-10">
        <DatePicker
          v-model="rawBox.unmountingDate"
          :error="errors?.unmountingDate"
          mode="datetime"
          :label="$t('box.unmountingDate')"
        />
        <Multiselect
          v-model="rawBox.status"
          class="w-1/2"
          :error="errors?.status"
          :options="[
            {
              label: $t('box.status.Destroyed'),
              value: 'Destroyed'
            },
            {
              label: $t('box.status.Lost'),
              value: 'Lost'
            },
            {
              label: $t('box.status.Other'),
              value: 'Other'
            },
            {
              label: $t('box.status.Returned'),
              value: 'Returned'
            },
          ]"
          :label="$t('box.unmountingReason')"
          :can-deselect="false"
          :can-clear="false"
        />
      </div>
      <div class="w-full text-center mb-10">
        <custom-button
          variant="outlined"
          class="mt-15 mr-5"
          @click="closeModal"
        >
          {{ $t('cancel') }}
        </custom-button>
        <custom-button
          variant="primary"
          class="mt-15"
          :busy="loading"
          @click="deleteBox"
        >
          {{ $t('delete') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import CustomButton from '@utils/CustomButton.vue';
import DatePicker from '@utils/inputs/DatePicker.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { date, object, string } from 'yup';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import type { Box, Driver } from '@/entities/Entities';
import type { ByReference, UpdateKeys } from '@/utils/types.js';

interface Props {
  open: boolean;
  box?: ByReference<Box, 'firstCampaign'>;
  driver: UpdateKeys<ByReference<Driver, 'region'>, { box?: ByReference<Box, 'firstCampaign'> }>;
}

const emit = defineEmits(['update:open', 'update:box', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalBox = useVModel(props, 'box', emit);

const { t } = useI18n();

const notify = useNotify();

const initialData = {
  unmountingDate: undefined,
  status: undefined,
};

const rawBox = ref({ ...initialData });

const {
  data: box,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    unmountingDate: date().nullable().required(t('validation.notEmpty')),
    status: string().nullable().required(t('validation.notEmpty')),
  }),
  rawBox,
);

function resetData() {
  rawBox.value = { ...initialData } as unknown as typeof rawBox['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const deleteBox = validatedFn(async () => {
  if (!props.box) return;

  try {
    await exec(props.box['@id'], {
      method: 'PUT',
      body: {
        unmountingDate: box.value.unmountingDate,
        status: box.value.status,
      },
    });

    await exec<Driver>(`/drivers/${props.driver.id}`, {
      method: 'PUT',
      body: {
        box: null,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.driver.details.sections.box.deleteBoxModal.deleteSuccess'),
    });

    internalBox.value = undefined;
    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.details.sections.box.deleteBoxModal.deleteError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
